/**
 * FormNumberField
 * ---------------
 * Polaris TextField (type=number) connected to React Hook Form.
 * Values are stored as numbers in the form but shown as strings in the input.
 */

import type { TextFieldProps } from "@shopify/polaris";
import { TextField } from "@shopify/polaris";
import { useController, useFormContext, type FieldValues, type Path } from "react-hook-form";
import { useVariantFieldDisabled } from "./context/VariantFieldContext";

export interface FormNumberFieldProps<TFieldValues extends FieldValues = FieldValues> extends Omit<
    TextFieldProps,
    "value" | "onChange" | "onBlur" | "error" | "autoComplete" | "type"
> {
    name: Path<TFieldValues> | (string & NonNullable<unknown>);
    label: string;
    min?: number;
    max?: number;
    step?: number;
    helpText?: string;
    disabled?: boolean;
    /** Override the value read from RHF (e.g. for array-item fields not registered as a real path). */
    value?: number | string;
    /** Override the write to RHF — receives the raw string typed + the underlying field, so callers can clamp/transform before writing. */
    onChange?: (value: string, field: ReturnType<typeof useController<TFieldValues>>["field"]) => void;
    onBlur?: (field: ReturnType<typeof useController<TFieldValues>>["field"]) => void;
    /** Override the error shown (e.g. for custom local-state validation instead of/in addition to schema validation). */
    error?: string | boolean;
}

export function FormNumberField<TFieldValues extends FieldValues = FieldValues>({
    name,
    label,
    min,
    max,
    step = 1,
    helpText,
    disabled,
    value,
    onChange,
    onBlur,
    error,
    ...rest
}: FormNumberFieldProps<TFieldValues>) {
    const { control } = useFormContext<TFieldValues>();

    const {
        field,
        fieldState: { error: fieldError },
    } = useController<TFieldValues>({ control, name: name as any });

    const isAbDisabled = useVariantFieldDisabled(name as string);

    return (
        <TextField
            {...rest}
            label={label}
            autoComplete="off"
            type="number"
            value={value !== undefined ? String(value) : String(field.value ?? "")}
            onChange={(val) => {
                if (onChange) {
                    onChange(val, field);
                } else {
                    const parsed = val === "" ? "" : Number(val);
                    field.onChange(parsed);
                }
            }}
            onBlur={() => (onBlur ? onBlur(field) : field.onBlur())}
            error={error !== undefined ? error : fieldError?.message}
            helpText={helpText}
            min={min}
            max={max}
            step={step}
            disabled={disabled || isAbDisabled}
        />
    );
}
